// ═══════════════════════════════════════════════════════════════════════════
// SSAG "Location within the Range" advisor — deterministic engine
// ═══════════════════════════════════════════════════════════════════════════
//
// Pure, offline, framework-agnostic. Asks PLAIN, FACTUAL questions a
// self-represented person can answer (no legal jargon), then INFERS the SSAG
// concepts — the strength of the compensatory claim, need, entitlement — and
// suggests where in the range the case sits, with reasons. No network, no LLM;
// this is the deterministic "brain" a conversational Claude layer will later
// sit on top of and be grounded by.
//
// PRIMARY SOURCES (verified 2026): DOJ SSAG Full Report (2008) + Revised User's
// Guide (2016): entitlement is a threshold decided BEFORE the formulas and a
// positive range does not create it (Ch. 4); non-exclusive factors fix a point
// in the range and the mid-point is NOT a default (Ch. 9); with-child cases
// usually land in the UPPER HALF (Ch. 8); ceiling ~$350k / floor ~$20k (Ch. 11);
// exceptions can move an award out of the range (Ch. 12).
//
// This is legal INFORMATION, not legal advice.

export const DISCLAIMER =
  'This is general information to help you think through the SSAG factors, not ' +
  'legal advice, and not a prediction of what a court will order. The SSAG are ' +
  'advisory guidelines; entitlement, the exact amount, and duration are ' +
  'ultimately legal questions. Consider advice from a family lawyer.'

// ─── The questions (plain-language, factual) ────────────────────────────────
// Each option carries a signed `weight` (POSITIVE → higher end of the range),
// a `reason` clause shown in the results, and a `basis` tag ('comp' | 'need')
// used to INFER entitlement from the facts. `when(context)` hides a question
// that doesn't apply (e.g. child-related questions with no children). The
// income gap and recipient age/disability are read from the data, not asked
// (see deriveAuto).
export const QUESTIONS = [
  // — Roles during the relationship → compensatory basis —
  {
    id: 'childcareTimeOff',
    section: 'Roles during the relationship',
    when: c => (c.childrenCount || 0) > 0,
    question: 'Did the recipient take time off work, or work less, to care for the children?',
    help: 'For example, staying home, going part-time, or turning down work to raise the kids.',
    options: [
      { value: 'alot', label: 'Yes, a lot', weight: 0.12, reason: 'significant time out of the workforce to raise the children (SSAG Ch. 9, compensatory)', basis: 'comp' },
      { value: 'some', label: 'Some',       weight: 0.06, reason: 'some time out of work for the children', basis: 'comp' },
      { value: 'no',   label: 'No',         weight: 0.00, reason: null },
    ],
  },
  {
    id: 'movedForCareer',
    section: 'Roles during the relationship',
    question: 'Did the recipient ever move, or leave or turn down a job, to support the payor’s career?',
    help: 'Relocating for the payor’s job, or giving up their own work so the payor could advance.',
    options: [
      { value: 'yes', label: 'Yes', weight: 0.08, reason: 'moving or giving up work to support the other’s career', basis: 'comp' },
      { value: 'no',  label: 'No',  weight: 0.00, reason: null },
    ],
  },
  {
    id: 'earningCapacityLower',
    section: 'Roles during the relationship',
    question: 'Because of decisions made during the relationship, can the recipient earn less now than they otherwise could?',
    help: 'e.g. missed promotions, skills gone stale, or having to restart a career.',
    options: [
      { value: 'yes',    label: 'Yes',    weight: 0.10, reason: 'a lasting hit to the recipient’s earning capacity (SSAG Ch. 9, compensatory)', basis: 'comp' },
      { value: 'unsure', label: 'Unsure', weight: 0.00, reason: null },
      { value: 'no',     label: 'No',     weight: -0.08, reason: 'the recipient’s earning capacity being largely intact' },
    ],
  },
  // — Recipient's current situation → need / self-sufficiency —
  {
    id: 'working',
    section: 'Recipient’s current situation',
    question: 'Is the recipient working now?',
    options: [
      { value: 'fulltime', label: 'Full-time', weight: -0.05, reason: null },
      { value: 'parttime', label: 'Part-time', weight: 0.04, reason: 'the recipient only working part-time', basis: 'need' },
      { value: 'no',       label: 'Not working', weight: 0.08, reason: 'the recipient not currently working', basis: 'need' },
    ],
  },
  {
    id: 'selfSuffTimeline',
    section: 'Recipient’s current situation',
    question: 'Realistically, how soon could the recipient support themselves at a reasonable standard?',
    options: [
      { value: 'now',      label: 'Already can', weight: -0.10, reason: 'the recipient being able to support themselves soon' },
      { value: 'fewyears', label: 'A few years / with training', weight: 0.06, reason: 'the recipient needing time or training to become self-sufficient', basis: 'need' },
      { value: 'unlikely', label: 'Unlikely', weight: 0.10, reason: 'limited prospect of the recipient becoming self-sufficient', basis: 'need' },
    ],
  },
  // — Health (SSAG Ch. 12.4 illness/disability) —
  // Health can be one of the most powerful facts in the range: a recipient who
  // cannot work because of illness has both deep need and no path to
  // self-sufficiency; a payor who cannot work has no ability to pay. Severity
  // matters, so the flat has-a-disability signal is replaced by a probe of how
  // the condition affects work. The screening question is skipped when the
  // data already shows a health signal (DTC flag or disability/WC income).
  {
    id: 'healthConcerns',
    section: 'Health',
    when: (c) => { const h = healthSignals(c); return !h.recipient && !h.payor },
    question: 'Does either of you have a health condition that affects daily life or work?',
    help: 'Physical or mental health, ongoing illness, or injury.',
    options: [
      { value: 'no',        label: 'No', weight: 0.00, reason: null },
      { value: 'recipient', label: 'Yes, the recipient', weight: 0.00, reason: null },
      { value: 'payor',     label: 'Yes, the payor', weight: 0.00, reason: null },
      { value: 'both',      label: 'Yes, both', weight: 0.00, reason: null },
    ],
  },
  {
    id: 'recipientHealthImpact',
    section: 'Health',
    when: (c, a = {}) => healthSignals(c).recipient
      || a.healthConcerns === 'recipient' || a.healthConcerns === 'both',
    question: 'How much does the recipient’s health condition affect their ability to work?',
    help: 'Think about what work is realistically possible now and in the next few years.',
    options: [
      { value: 'cannot',    label: 'Cannot work',            weight: 0.18, reason: 'the recipient being unable to work because of their health (SSAG Ch. 12.4)', basis: 'need' },
      { value: 'reduced',   label: 'Can work, but less',     weight: 0.10, reason: 'the recipient’s health limiting how much they can work', basis: 'need' },
      { value: 'temporary', label: 'Temporary, should recover', weight: 0.04, reason: 'a temporary health setback for the recipient', basis: 'need' },
      { value: 'minimal',   label: 'Little or no effect',    weight: 0.00, reason: null },
    ],
  },
  {
    id: 'payorHealthImpact',
    section: 'Health',
    when: (c, a = {}) => healthSignals(c).payor
      || a.healthConcerns === 'payor' || a.healthConcerns === 'both',
    question: 'How much does the payor’s health condition affect their ability to work?',
    help: 'A payor who cannot earn has less ability to pay, now and going forward.',
    options: [
      { value: 'cannot',    label: 'Cannot work',            weight: -0.16, reason: 'the payor being unable to work because of their health (SSAG Ch. 12.4)' },
      { value: 'reduced',   label: 'Can work, but less',     weight: -0.10, reason: 'the payor’s health limiting how much they can work' },
      { value: 'temporary', label: 'Temporary, should recover', weight: -0.04, reason: 'a temporary health setback for the payor' },
      { value: 'minimal',   label: 'Little or no effect',    weight: 0.00, reason: null },
    ],
  },
  // — Property & other circumstances —
  {
    id: 'propertyShare',
    section: 'Property & other',
    question: 'When property is divided, roughly what will the recipient end up with?',
    help: 'Home, savings, pensions, minus debts.',
    options: [
      { value: 'less', label: 'Less than half', weight: 0.10, reason: 'the recipient receiving less than half of the property (SSAG Ch. 9, property division)', basis: 'need' },
      { value: 'half', label: 'About half',     weight: 0.00, reason: null },
      { value: 'more', label: 'More than half', weight: -0.12, reason: 'the recipient receiving more than half of the property (SSAG Ch. 9, property division)' },
    ],
  },
  {
    id: 'repartner',
    section: 'Property & other',
    question: 'Is the recipient now living with a new partner?',
    options: [
      { value: 'no',  label: 'No',  weight: 0.00, reason: null },
      { value: 'yes', label: 'Yes', weight: -0.12, reason: 'the recipient now living with a new partner (SSAG Ch. 14, repartnering)' },
    ],
  },
  {
    id: 'payorAccessCosts',
    section: 'Property & other',
    when: c => (c.childrenCount || 0) > 0,
    question: 'Does the payor have high costs to see the children (e.g. long-distance travel)?',
    options: [
      { value: 'no',  label: 'No',  weight: 0.00, reason: null },
      { value: 'yes', label: 'Yes', weight: -0.08, reason: 'the payor’s high costs to see the children' },
    ],
  },
  {
    id: 'payorOtherSupport',
    section: 'Property & other',
    // Skipped when the income entries already show prior-relationship support
    // paid by the payor — deriveAuto applies the factor from the data instead.
    when: c => !(c.incomeSignals && c.incomeSignals.payorPriorSupport > 0),
    question: 'Does the payor support children from another relationship, or pay other support?',
    options: [
      { value: 'no',  label: 'No',  weight: 0.00, reason: null },
      { value: 'yes', label: 'Yes', weight: -0.10, reason: 'the payor’s other support obligations' },
    ],
  },
  {
    id: 'childSpecialNeeds',
    section: 'Property & other',
    // Only asked when there are children AND no child is already flagged as
    // having a disability in the calculation (that flag is folded in by
    // deriveAuto without asking).
    when: c => (c.childrenCount || 0) > 0 && !(c.childSignals && c.childSignals.anyChildDisabled),
    question: 'Do any of the children have special needs or unusually high costs?',
    help: 'For example a disability, medical needs, or intensive programs beyond the usual activities.',
    options: [
      { value: 'no',  label: 'No',  weight: 0.00, reason: null },
      { value: 'yes', label: 'Yes', weight: 0.08, reason: 'a child with special needs raising the caregiving parent’s costs', basis: 'need' },
    ],
  },
  {
    id: 'interimStage',
    section: 'Property & other',
    question: 'Is this an interim (temporary) arrangement while the case is still being resolved?',
    help: 'Support set before a final agreement or trial, to hold things over in the meantime.',
    options: [
      { value: 'no',  label: 'No, final',  weight: 0.00, reason: null },
      { value: 'yes', label: 'Yes, interim', weight: 0.00, reason: null },
    ],
  },
  {
    id: 'payorDebts',
    section: 'Property & other',
    question: 'Does the payor have unusually high debts or required deductions?',
    help: 'Beyond ordinary living costs, e.g. large mandatory pension contributions or debt payments.',
    options: [
      { value: 'no',  label: 'No',  weight: 0.00, reason: null },
      { value: 'yes', label: 'Yes', weight: -0.10, reason: 'the payor’s limited ability to pay from debts and deductions (SSAG Ch. 12, debt exception)' },
    ],
  },
  {
    id: 'agreement',
    section: 'Property & other',
    question: 'Is there a written agreement (marriage or cohabitation agreement) that deals with support?',
    options: [
      { value: 'no',     label: 'No',     weight: 0.00, reason: null },
      { value: 'yes',    label: 'Yes',    weight: 0.00, reason: null, basis: 'contract' },
      { value: 'unsure', label: 'Unsure', weight: 0.00, reason: null },
    ],
  },
]

// Questions that actually apply to this case (drops child-only questions when
// there are no children; health severity questions appear only when a health
// signal exists in the data or the screening answer says so). Drives the
// guided flow. `when(context, answers)` gates may read earlier answers, so the
// flow re-filters as answers land.
export function askQuestions(context = {}, answers = {}) {
  return QUESTIONS.filter(q => typeof q.when !== 'function' || q.when(context, answers))
}

// Health signals already present in the DATA (DTC flags or disability/WC
// income types) — used to skip the screening question and go straight to the
// severity probe.
export function healthSignals(context = {}) {
  const sig = context.incomeSignals || {}
  return {
    recipient: !!context.recipientDisabled || (sig.recipientDisabilityIncome || 0) > 0,
    payor: !!context.payorDisabled || (sig.payorDisabilityIncome || 0) > 0,
  }
}

// Blank answers object with every question id pre-created (Vue 2 reactivity
// needs the keys to exist up front). Shared by the advisor component and the
// calculator's tabbed workspace, which lifts the answers into its own state
// so the Circumstances and Recommendation tabs stay in sync.
export function makeEmptyAnswers() {
  const a = {}
  for (const q of QUESTIONS) a[q.id] = null
  return a
}

// Facts the calculator already knows — folded in without asking.
//   • income gap  → recipient's income as a % of the payor's
//   • recipient age (near/over 60) and disability → weigh on self-sufficiency
export function deriveAuto(context = {}, answers = {}) {
  const p = Number(context.payorIncome) || 0
  const r = Number(context.recipientIncome) || 0
  const out = { weight: 0, notes: [], needFromGap: false }
  // When the graduated health questions are answered, THEY carry the health
  // weight (severity-scaled); the flat data signals below then contribute
  // notes only, not weight, so health is never double-counted.
  const recipHealthAnswered = answers.recipientHealthImpact != null
  const payorHealthAnswered = answers.payorHealthImpact != null
  if (p > 0) {
    const pct = Math.round((r / p) * 100)
    if (pct < 40) { out.weight += 0.12; out.needFromGap = true; out.notes.push(`Income gap looks large: the recipient earns about ${pct}% of the payor’s income. (SSAG Ch. 9, needs and means)`) }
    else if (pct > 75) { out.weight += -0.10; out.notes.push(`Incomes are close: the recipient earns about ${pct}% of the payor’s income. (SSAG Ch. 9)`) }
    else { out.needFromGap = true; out.notes.push(`Moderate income gap: the recipient earns about ${pct}% of the payor’s income. (SSAG Ch. 9)`) }
  }
  if (context.recipientDisabled) {
    if (!recipHealthAnswered) out.weight += 0.10
    out.notes.push('The recipient has a disability, which can increase need and limit self-sufficiency. (SSAG Ch. 12.4)')
  }
  if (Number(context.recipientAge) >= 60) { out.weight += 0.06; out.notes.push('The recipient is near or past typical retirement age, which limits self-sufficiency. (SSAG Ch. 7)') }

  // ── Circumstances the income entries already show (SSAG Ch. 9 / 12) ──────
  // These are read from the calculation's income types so nobody is asked
  // about facts already entered.
  const sig = context.incomeSignals || {}
  const mo = (n) => Math.round((Number(n) || 0) / 12).toLocaleString('en-CA')
  if (sig.payorPriorSupport > 0) {
    out.weight += -0.10
    out.notes.push(`The payor already pays about $${mo(sig.payorPriorSupport)}/month in support for a prior family, a first charge on income that limits ability to pay (SSAG Ch. 12).`)
  }
  if (sig.recipientPriorSupportReceived > 0) {
    out.weight += -0.04
    out.notes.push(`The recipient receives about $${mo(sig.recipientPriorSupportReceived)}/month in support from a prior relationship, which already meets part of their need. (SSAG Ch. 9)`)
  }
  if (sig.recipientDisabilityIncome > 0 && !context.recipientDisabled) {
    if (!recipHealthAnswered) out.weight += 0.08
    out.needFromGap = true
    out.notes.push('The recipient’s income includes disability or workers’ compensation benefits, which points to illness or injury limiting self-sufficiency (SSAG Ch. 12).')
  }
  if (sig.recipientSocialAssistance > 0) {
    out.weight += 0.08
    out.needFromGap = true
    out.notes.push('The recipient is on social assistance, a strong marker of need; spousal support usually displaces assistance dollar for dollar. (SSAG Ch. 9)')
  }
  if (sig.recipientEI > 0) {
    out.weight += 0.04
    out.notes.push('The recipient is on Employment Insurance, a temporary interruption in earnings; consider a review once regular income resumes. (SSAG Ch. 14, review)')
  }
  if (sig.payorDisabilityIncome > 0 || context.payorDisabled) {
    if (!payorHealthAnswered) out.weight += -0.08
    out.notes.push('The payor has a disability or disability income, which can limit ability to pay and job prospects. (SSAG Ch. 12.4)')
  }
  if (sig.payorRetirementIncome > 0 || sig.recipientRetirementIncome > 0) {
    out.notes.push('Pension or retirement income is already in the calculation. The SSAG are built on working incomes; on or after retirement, support is usually revisited on actual retirement incomes.')
  }
  const cs = context.childSignals || {}
  if (cs.anyChildDisabled) {
    out.weight += 0.08
    out.needFromGap = true
    out.notes.push('A child in the calculation has a disability. A special-needs child raises the caregiving parent’s costs and can justify a higher amount or longer duration (SSAG Ch. 12).')
  }
  // Work incentives (SSAG Ch. 9): the award should leave the payor a
  // meaningful share of the family's disposable income.
  if (context.payorNdiShareHigh != null && context.payorNdiShareHigh < 0.50) {
    out.weight += -0.05
    out.notes.push(`At the top of the range the payor keeps about ${Math.round(context.payorNdiShareHigh * 100)}% of the family’s net disposable income, which strains work incentives and cautions against the very top. (SSAG Ch. 9, work incentives)`)
  }

  // ── Budget signals (Needs tab) ──────────────────────────────────────────
  // When party budgets are entered, "needs and means" stop being abstractions:
  // we can compare each party's monthly living expenses to their net
  // disposable income at the low, mid, and high points of the range.
  //   • recipientShortfall{Low,Mid,High}: recipient budget minus recipient NDI
  //     at that point (positive = budget not met).
  //   • payorShortfallMid: payor budget minus payor NDI at the mid point
  //     (positive = the payor cannot meet their own budget at mid).
  // A shortfall that persists at the top of the range quantifies need
  // (pushes higher); a budget already met at the low end argues the low end
  // suffices; a payor who cannot cover their own budget is a means limit
  // (pushes lower). All figures are monthly dollars.
  const needs = context.needs || null
  if (needs && needs.hasBudgets) {
    const fmt = (n) => Math.round(Math.abs(n)).toLocaleString('en-CA')
    if (needs.recipientBudget > 0) {
      const sfMid = Number(needs.recipientShortfallMid) || 0
      const sfHigh = Number(needs.recipientShortfallHigh) || 0
      const sfLow = Number(needs.recipientShortfallLow) || 0
      if (sfHigh > 100) {
        out.weight += 0.14
        out.needFromGap = true
        out.notes.push(`The recipient's budget shows a shortfall of about $${fmt(sfHigh)}/month even at the top of the range, which quantifies ongoing need. (SSAG Ch. 9, needs)`)
      } else if (sfMid > 100) {
        out.weight += 0.08
        out.needFromGap = true
        out.notes.push(`The recipient's budget shows a shortfall of about $${fmt(sfMid)}/month at the mid range; the shortfall closes only in the upper part of the range. (SSAG Ch. 9, needs)`)
      } else if (sfLow <= 0) {
        out.weight += -0.08
        out.notes.push(`The recipient's budget is met even at the low end of the range (about $${fmt(sfLow)}/month to spare), which supports a lower position. (SSAG Ch. 9)`)
      }
    }
    // The payor's needs matter too (Divorce Act s.15.2(4): the condition,
    // means, needs and circumstances OF EACH SPOUSE; SSAG Ch. 9 ability to
    // pay). A payor short even at the LOW end points at or below the range;
    // a payor comfortable even at the HIGH end is demonstrated ability to pay.
    if (needs.payorBudget > 0) {
      const pLow = Number(needs.payorShortfallLow) || 0
      const pMid = Number(needs.payorShortfallMid) || 0
      const pHigh = Number(needs.payorShortfallHigh) || 0
      if (pLow > 100) {
        out.weight += -0.16
        out.notes.push(`The payor's own budget shows a shortfall of about $${fmt(pLow)}/month even at the low end of the range. Ability to pay constrains the whole range; an amount at or below the low end, or an exceptions analysis, may be needed. (SSAG Ch. 9 ability to pay; Ch. 12 exceptions)`)
      } else if (pMid > 100) {
        out.weight += -0.10
        out.notes.push(`The payor's own budget shows a shortfall of about $${fmt(pMid)}/month at the mid range, which limits ability to pay. (SSAG Ch. 9, ability to pay)`)
      } else if (pHigh <= -400) {
        out.weight += 0.05
        out.notes.push(`The payor's budget is met with about $${fmt(pHigh)}/month to spare even at the top of the range, which shows the ability to pay a higher amount. (SSAG Ch. 9)`)
      }
    }
  }
  return out
}

function clamp(x, lo, hi) { return Math.min(hi, Math.max(lo, x)) }

function durationPositionLabel(score) {
  if (score < 0.35) return 'Shorter end'
  if (score <= 0.65) return 'Mid-range'
  return 'Longer end'
}

// Build the duration recommendation (SSAG Ch. 7 / Ch. 8). Uses only facts we
// already have: the calculator's duration range + indefinite flag, plus the
// self-sufficiency / age / disability / role answers the SSAG ties duration to.
function buildDurationAdvice(context = {}, answers = {}, auto = {}) {
  const years = Number(context.yearsMarried) || 0
  const age = Number(context.recipientAge) || 0
  const withChild = context.formula === 'with-child'
  const rationale = []

  // Why is it indefinite? (SSAG "no time limit" tests.)
  let indefiniteReason = null
  if (context.indefinite) {
    if (years >= 20) indefiniteReason = 'the 20-year rule (relationship of 20 years or more)'
    else if (age && years >= 5 && (years + age) >= 65) indefiniteReason = 'the Rule of 65 (years together plus the recipient’s age at separation ≥ 65)'
  }

  if (context.indefinite) {
    rationale.push(indefiniteReason
      ? `Duration is indefinite under ${indefiniteReason}. There is no fixed end date, but "indefinite" means open-ended and reviewable, not necessarily permanent (SSAG Ch. 7–8).`
      : 'Duration is indefinite (no fixed end date). It is open-ended and subject to later review or variation, not necessarily permanent (SSAG Ch. 7–8).')
    if ((answers.selfSuffTimeline === 'now' || answers.selfSuffTimeline === 'fewyears'))
      rationale.push('Because self-sufficiency looks achievable, expect a review once the recipient can support themselves, even though there is no fixed end now.')
    return {
      indefinite: true,
      indefiniteReason,
      low: context.durationLow != null ? context.durationLow : null,
      high: context.durationHigh != null ? context.durationHigh : null,
      suggestedYears: null,
      positionLabel: null,
      testNote: null,
      retirementNote: retirementNote(context),
      rationale,
    }
  }

  const low = Number(context.durationLow) || 0
  const high = Number(context.durationHigh) || 0
  const hasRange = high > low

  // Score WHERE in the duration range the facts point (0 = shortest, 1 = longest).
  let d = 0.5
  switch (answers.selfSuffTimeline) {
    case 'now':      d -= 0.35; rationale.push('the recipient being able to support themselves soon points to the shorter end'); break
    case 'fewyears': d -= 0.05; rationale.push('the recipient needing a few years / retraining points to the middle'); break
    case 'unlikely': d += 0.30; rationale.push('limited prospect of self-sufficiency points to the longer end'); break
  }
  if (answers.earningCapacityLower === 'yes') { d += 0.10; rationale.push('a lasting hit to earning capacity supports a longer term') }
  else if (answers.earningCapacityLower === 'no') { d -= 0.08 }
  if (answers.childcareTimeOff === 'alot') { d += 0.10; rationale.push('significant time out of the workforce for the children supports a longer term') }
  else if (answers.childcareTimeOff === 'some') { d += 0.05 }
  if (age >= 60) { d += 0.15; rationale.push('the recipient being near or past retirement age supports a longer term') }
  // Health: severity-scaled when the health questions were answered; the flat
  // disability flag is the fallback (never both).
  if (answers.recipientHealthImpact === 'cannot') { d += 0.25; rationale.push('the recipient being unable to work because of their health strongly supports a longer term') }
  else if (answers.recipientHealthImpact === 'reduced') { d += 0.12; rationale.push('the recipient’s health limiting their work supports a longer term') }
  else if (answers.recipientHealthImpact === 'temporary') { d += 0.03 }
  else if (answers.recipientHealthImpact == null && context.recipientDisabled) { d += 0.15; rationale.push('the recipient’s disability supports a longer term') }
  d = clamp(d, 0, 1)

  const suggestedYears = hasRange ? Math.round((low + d * (high - low)) * 2) / 2 : (high || low || null)

  // With-child age-of-children test explanation (SSAG Ch. 8).
  let testNote = null
  if (withChild && context.durationTestApplied === 'age-of-children') {
    testNote = 'This uses the age-of-children test: the lower end runs until the youngest child starts full-time school, the upper end until the last child finishes high school. The longer of that and the length-of-marriage test governs (SSAG Ch. 8).'
  } else if (!withChild) {
    testNote = 'Under the without-child formula, duration runs about ½ to 1 year for each year of the relationship (SSAG Ch. 7).'
  }

  return {
    indefinite: false,
    indefiniteReason: null,
    low, high,
    suggestedYears,
    positionLabel: hasRange ? durationPositionLabel(d) : null,
    testNote,
    retirementNote: retirementNote(context),
    rationale,
  }
}

// Retirement note (SSAG do not extend cleanly past retirement). Shown on
// indefinite orders and whenever someone is near retirement age, because
// retirement is the most common reason to review or reduce ongoing support.
// The SSAG treat retirement as a change in circumstances, not a formula input.
function retirementNote(context = {}) {
  const age = Number(context.recipientAge) || 0
  if (!context.indefinite && age < 60) return null
  return 'Retirement is a common reason to review or reduce support: the SSAG are built on working incomes and do not extend cleanly past retirement, so support is usually recalculated on actual retirement incomes. If a pension was already divided as property at separation, that same income generally cannot be counted again for support (the double-dipping rule). Indefinite does not mean permanent.'
}

function positionLabel(score) {
  if (score < 0.30) return 'Low end'
  if (score < 0.45) return 'Lower-mid'
  if (score < 0.55) return 'Mid-range'
  if (score <= 0.70) return 'Upper-mid'
  return 'High end'
}

// Main entry point.
//   context : { applicable, formula, low, mid, high, durationLow, durationHigh,
//               indefinite, yearsMarried, recipientAge, recipientDisabled,
//               childrenCount, parentingArrangement ('primary'|'shared'|'hybrid'|'split'|'none'),
//               payorIncome, recipientIncome,
//               aboveCeiling, belowFloor, inLowIncomeBand, payorName, recipientName }
//   answers : { [questionId]: optionValue }
// Never throws on missing input.
export function computeAdvice(context = {}, answers = {}) {
  const withChild = context.formula === 'with-child'
  const applicable = askQuestions(context, answers)
  const auto = deriveAuto(context, answers)

  // Boundary / interpretation notes (SSAG Ch. 11–12).
  const boundaryNotes = []
  const rangeIsZero = !context.low && !context.mid && !context.high
  if (context.aboveCeiling) boundaryNotes.push('Payor income is above the SSAG ceiling (~$350,000). Above the ceiling the range is a guide only, and a court has broad discretion (SSAG Ch. 11).')
  if (context.belowFloor) boundaryNotes.push('Payor income is below the SSAG floor (~$20,000). SSAG generally indicates no spousal support at this level (SSAG Ch. 11).')
  else if (context.inLowIncomeBand) boundaryNotes.push('Payor income is in the $20,000–$30,000 discretionary band, so support may be below the range or nil (SSAG Ch. 11).')
  if (rangeIsZero) boundaryNotes.push('The range is $0, often because child support has consumed ability to pay. Entitlement can still exist; the limit is money, not principle (SSAG Ch. 8).')
  if (answers.interimStage === 'yes') boundaryNotes.push('This is an interim arrangement. At the interim stage courts focus on cash flow and holding the status quo rather than a precise final analysis, and compelling financial circumstances can justify departing from the range until trial or agreement (SSAG Ch. 12.1).')
  if (answers.recipientHealthImpact === 'cannot') boundaryNotes.push('The recipient cannot work because of their health. The illness and disability exception (SSAG Ch. 12.4) recognizes that serious long-term illness can justify an amount or duration beyond the usual range, especially where self-sufficiency is not realistic.')
  if (answers.payorHealthImpact === 'cannot') boundaryNotes.push('The payor cannot work because of their health. Ability to pay rests on actual income, not past earning power; if the payor’s income has not yet been updated to reflect this, revisit the incomes on the Means tab.')

  // Infer entitlement from the facts (SSAG Ch. 4).
  let hasComp = false, hasNeed = auto.needFromGap, hasContract = false
  for (const q of applicable) {
    const opt = (q.options || []).find(o => o.value === answers[q.id])
    if (!opt) continue
    if (opt.basis === 'comp') hasComp = true
    if (opt.basis === 'need') hasNeed = true
    if (opt.basis === 'contract') hasContract = true
  }
  const answeredCore = ['childcareTimeOff', 'movedForCareer', 'earningCapacityLower', 'working', 'selfSuffTimeline']
    .filter(id => applicable.some(q => q.id === id)).every(id => answers[id] != null)

  // Score: with-child biases to the upper half; mid is NOT a default. Add the
  // answered questions + the auto (income gap / age / disability) contribution.
  let score = withChild ? 0.62 : 0.50
  const reasonsHigh = []
  const reasonsLow = []
  for (const q of applicable) {
    const opt = (q.options || []).find(o => o.value === answers[q.id])
    if (!opt) continue
    score += opt.weight
    if (opt.reason && opt.weight > 0) reasonsHigh.push(opt.reason)
    if (opt.reason && opt.weight < 0) reasonsLow.push(opt.reason)
  }
  score += auto.weight
  score = clamp(score, 0, 1)

  const low = context.low || 0
  const high = context.high || 0
  const dollar = Math.round((low + score * (high - low)) / 10) * 10

  // Entitlement summary (plain language).
  const bases = []
  if (hasComp) bases.push('compensatory (roles/career sacrifice)')
  if (hasNeed) bases.push('needs-based (income gap / dependency)')
  if (hasContract) bases.push('a written agreement')
  const hasEntitlement = hasComp || hasNeed || hasContract
  const entitlementNote = hasEntitlement
    ? `Based on the answers, a basis for support appears to exist: ${bases.join('; ')}. Entitlement is ultimately a legal question, but these facts point toward it.`
    : (answeredCore
        ? 'The answers don’t clearly show a basis for support (no career sacrifice, small income gap, recipient self-sufficient). A positive range alone doesn’t create entitlement; get legal advice before relying on a number.'
        : 'Answer the questions to assess whether there’s a basis for support.')

  // Parenting-arrangement note (with-child formula only). Parenting is a
  // FORMULA input, not a Ch. 9 location factor — the range the user sees already
  // reflects it (set-off CS, the 50/50 NDI high-end extension). We surface the
  // practical read on where in that range shared/hybrid cases tend to sit.
  let parentingNote = null
  if (withChild) {
    const arr = context.parentingArrangement
    if (arr === 'shared') {
      parentingNote = 'This is a shared-parenting case. The range already reflects set-off child support, and the high end reflects the 50/50 net-income cap (SSAG Ch. 8.6). The cost of maintaining two near-equal households is a recognized reason to look at the upper part of the range.'
    } else if (arr === 'hybrid') {
      parentingNote = 'This is a hybrid case (at least one child in shared parenting, at least one in primary care). The range already blends set-off and full table amounts; the high end reflects the 50/50 net-income cap (SSAG Ch. 8). The cost of two near-equal households can support a higher position.'
    } else if (arr === 'split') {
      parentingNote = 'This is a split-parenting case (a child living primarily with each parent). The range already nets the two table obligations against each other (SSAG Ch. 8); confirm the set-off matches the intended arrangement.'
    }
  }

  // ── Duration advice (SSAG Ch. 7 without-child / Ch. 8 with-child) ──
  // The calculator already computes the duration RANGE and whether it's
  // indefinite. Here we say WHERE in that range the facts point, using the same
  // self-sufficiency / age / disability signals the SSAG ties duration to.
  const duration = buildDurationAdvice(context, answers, auto)
  // Back-compat single-line note (kept for any older consumer / grounding).
  const durationNote = context.indefinite
    ? 'Duration is indefinite (subject to review), and amount and duration can be traded off via restructuring (SSAG Ch. 10).'
    : (context.durationHigh && context.durationLow != null
        ? `Duration range is ${context.durationLow.toFixed(1)}–${context.durationHigh.toFixed(1)} years. A higher amount can justify a shorter term and vice versa (SSAG Ch. 9–10).`
        : null)

  return {
    hasEntitlement,
    entitlementNote,
    position: hasEntitlement ? score : null,
    positionLabel: hasEntitlement ? positionLabel(score) : null,
    dollar: hasEntitlement ? dollar : null,
    withChildUpperHalfNote: (hasEntitlement && withChild)
      ? 'With-child cases usually resolve in the upper half of the range; the mid-point is not a default (SSAG Ch. 8).' : null,
    reasonsHigh,
    reasonsLow,
    autoNotes: auto.notes,
    boundaryNotes,
    parentingNote,
    durationNote,
    duration,
    disclaimer: DISCLAIMER,
  }
}
